import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import { isSmartAccount } from '@ambire-common/libs/account/account'
import { humanizeMessage } from '@ambire-common/libs/humanizer'
import ErrorOutlineIcon from '@common/assets/svg/ErrorOutlineIcon'
import PlainAddressWithCopy from '@common/components/AccountAddress/PlainAddressWithCopy'
import Alert from '@common/components/Alert'
import Avatar from '@common/components/Avatar'
import ExpandableCard from '@common/components/ExpandableCard'
import HumanizedVisualization from '@common/components/HumanizedVisualization'
import Label from '@common/components/Label'
import NetworkBadge from '@common/components/NetworkBadge'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import useWindowSize from '@common/hooks/useWindowSize'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import spacings, { SPACING_LG, SPACING_MD, SPACING_TY } from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import flexbox from '@common/styles/utils/flexbox'
import { TabLayoutWrapperMainContent } from '@web/components/TabLayoutWrapper'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import useResponsiveActionWindow from '@web/hooks/useResponsiveActionWindow'
import useSignMessageControllerState from '@web/hooks/useSignMessageControllerState'
import HardwareWalletSigningModal from '@web/modules/hardware-wallet/components/HardwareWalletSigningModal'
import LedgerConnectModal from '@web/modules/hardware-wallet/components/LedgerConnectModal'
import { QrRequest } from '@ambire-common/interfaces/keystore'
import { QrSigningStep } from '@web/modules/hardware-wallet/qr/types'
import QrSigningFlowScreen from '@web/modules/hardware-wallet/screens/QrSigningFlowScreen'
import FallbackVisualization from '@web/modules/sign-message/screens/SignMessageScreen/FallbackVisualization'
import GrantApprovalClause from '@web/modules/sign-message/screens/SignMessageScreen/GrantApprovalClause'
import Info from '@web/modules/sign-message/screens/SignMessageScreen/Info'
import getStyles from '@web/modules/sign-message/screens/SignMessageScreen/styles'

interface Props {
  shouldDisplayLedgerConnectModal: boolean
  isLedgerConnected: boolean
  handleDismissLedgerConnectModal: () => void
  hasReachedBottom: boolean | null
  setHasReachedBottom: Dispatch<SetStateAction<boolean | null>>
  shouldDisplayEIP1271Warning: boolean
  shouldDisplayQrSigningModal: boolean
  currentRequest: QrRequest | null
  signingStep: QrSigningStep
  handleQrSigningFlowOnClosePressed: () => void
  handleQrSingingFlowOnContinuePressed: () => void
  handleQrSigningFlowSubmitSignatureResponse: (payload: string | Uint8Array) => void
  handleQrSigningFlowOnRejectPressed: () => void
  handleQrSigningFlowOnBackPressed: () => void
}

const Main = ({
  shouldDisplayLedgerConnectModal,
  isLedgerConnected,
  handleDismissLedgerConnectModal,
  hasReachedBottom,
  setHasReachedBottom,
  shouldDisplayEIP1271Warning,
  shouldDisplayQrSigningModal,
  currentRequest,
  signingStep,
  handleQrSigningFlowOnClosePressed,
  handleQrSingingFlowOnContinuePressed,
  handleQrSigningFlowSubmitSignatureResponse,
  handleQrSigningFlowOnRejectPressed,
  handleQrSigningFlowOnBackPressed
}: Props) => {
  const { t } = useTranslation()
  const { dispatch } = useBackgroundService()
  const signMessageState = useSignMessageControllerState()
  const signStatus = signMessageState.statuses.sign
  const { styles, theme, themeType } = useTheme(getStyles)
  const { responsiveSizeMultiplier } = useResponsiveActionWindow()
  const { minHeightSize } = useWindowSize()
  const { networks } = useNetworksControllerState()
  const { account } = useSelectedAccountControllerState()
  const network = useMemo(
    () =>
      networks.find((n) => {
        return signMessageState.messageToSign?.content.kind === 'typedMessage' &&
          signMessageState.messageToSign?.content.domain.chainId
          ? n.chainId.toString() ===
              signMessageState.messageToSign?.content.domain.chainId.toString()
          : n.chainId === signMessageState.messageToSign?.chainId
      }),
    [networks, signMessageState.messageToSign]
  )
  const humanizedMessage = useMemo(() => {
    if (!signMessageState?.messageToSign) return
    return humanizeMessage(signMessageState.messageToSign)
  }, [signMessageState])
  const visualizeHumanized = useMemo(
    () =>
      humanizedMessage?.fullVisualization &&
      network &&
      signMessageState.messageToSign?.content.kind,
    [network, humanizedMessage, signMessageState.messageToSign?.content?.kind]
  )

  // A token-approval (permit) visualization → render the design's "Grant
  // approval" clause. Bridge intents / messages without a token item fall back
  // to the generic visualization below.
  const approvalVisualization = useMemo(
    () =>
      visualizeHumanized && humanizedMessage?.fullVisualization?.some((i) => i?.type === 'token')
        ? humanizedMessage.fullVisualization
        : null,
    [visualizeHumanized, humanizedMessage]
  )

  return (
    <TabLayoutWrapperMainContent style={spacings.mbLg}>
      {/* Title + EIP-712 kind pill. */}
      <View
        style={[
          flexbox.directionRow,
          flexbox.alignCenter,
          {
            marginBottom: SPACING_MD * responsiveSizeMultiplier
          }
        ]}
      >
        <Text weight="semiBold" fontSize={22 * responsiveSizeMultiplier} style={[spacings.mrSm]}>
          {t('Sign message')}
        </Text>
        <View style={styles.kindOfMessage}>
          <Text fontSize={12} weight="medium" color={theme.infoText} numberOfLines={1}>
            {signMessageState.messageToSign?.content.kind === 'typedMessage' &&
              t('EIP-712 typed data')}
            {signMessageState.messageToSign?.content.kind === 'message' && t('Standard message')}
            {signMessageState.messageToSign?.content.kind === 'authorization-7702' &&
              t('EIP-7702 authorization')}
          </Text>
        </View>
      </View>

      {/* Account + chain card (design's account row, below the title). */}
      {!!account && (
        <View
          style={[
            flexbox.directionRow,
            flexbox.alignCenter,
            flexbox.justifySpaceBetween,
            {
              gap: 12,
              marginBottom: SPACING_MD * responsiveSizeMultiplier,
              paddingVertical: 12,
              paddingHorizontal: 14,
              borderRadius: RADII.md,
              backgroundColor: theme.controlSurfaceBackground as string
            }
          ]}
        >
          <View style={[flexbox.directionRow, flexbox.alignCenter, flexbox.flex1, { gap: 12 }]}>
            <Avatar
              address={account.addr}
              pfp={account.preferences.pfp}
              isSmart={isSmartAccount(account)}
              size={38}
            />
            <View style={flexbox.flex1}>
              {!!account.preferences?.label && (
                <Text fontSize={14} weight="semiBold" numberOfLines={1}>
                  {account.preferences.label}
                </Text>
              )}
              <PlainAddressWithCopy address={account.addr} maxLength={16} hideParentheses />
            </View>
          </View>
          <NetworkBadge
            chainId={signMessageState.messageToSign?.chainId}
            responsiveSizeMultiplier={responsiveSizeMultiplier}
            withOnPrefix
          />
        </View>
      )}
      <View style={styles.container}>
        <View
          style={{
            marginBottom: SPACING_LG * responsiveSizeMultiplier
          }}
        >
          <Info />
          {shouldDisplayEIP1271Warning && (
            <Alert
              type="error"
              title="This app has been flagged to not support Smart Account signatures."
              text="If you encounter issues, please use an EOA account and contact the app to resolve this."
            />
          )}
        </View>
        <View style={flexbox.flex1}>
          {approvalVisualization ? (
            <GrantApprovalClause
              fullVisualization={approvalVisualization}
              chainId={network?.chainId || 1n}
              accent={theme.successDecorative as string}
            />
          ) : (
          <ExpandableCard
            enableToggleExpand={!!visualizeHumanized}
            isInitiallyExpanded={!visualizeHumanized}
            hasArrow={!!visualizeHumanized}
            style={{
              marginBottom: SPACING_TY * responsiveSizeMultiplier,
              // Setting maxHeight on larger screens introduced internal content scroll
              // (which aligns the content better - with internal scrollbar).
              ...(minHeightSize(660) ? {} : { maxHeight: '100%' }),
              borderRadius: RADII.md,
              borderWidth: 1,
              borderColor: theme.controlSurfaceBorder as string,
              backgroundColor:
                themeType === THEME_TYPES.DARK
                  ? (theme.controlSurfaceBackground as string)
                  : (theme.primaryBackground as string),
              ...(humanizedMessage?.warnings?.length ? styles.warningContainer : {})
            }}
            content={
              visualizeHumanized &&
              // @TODO: Duplicate check. For some reason ts throws an error if we don't do this
              humanizedMessage?.fullVisualization &&
              signMessageState.messageToSign?.content.kind ? (
                <HumanizedVisualization
                  data={humanizedMessage.fullVisualization}
                  chainId={network?.chainId || 1n}
                  sizeMultiplierSize={responsiveSizeMultiplier}
                />
              ) : (
                <>
                  <View
                    style={{
                      marginRight: SPACING_TY * responsiveSizeMultiplier
                    }}
                  >
                    <ErrorOutlineIcon
                      width={24 * responsiveSizeMultiplier}
                      height={24 * responsiveSizeMultiplier}
                    />
                  </View>
                  <Text fontSize={14 * responsiveSizeMultiplier} appearance="warningText">
                    <Text
                      fontSize={14 * responsiveSizeMultiplier}
                      appearance="warningText"
                      weight="semiBold"
                    >
                      {t('Warning: ')}
                    </Text>
                    {t('Please read the whole message as we are unable to translate it!')}
                  </Text>
                </>
              )
            }
            expandedContent={
              <FallbackVisualization
                setHasReachedBottom={setHasReachedBottom}
                hasReachedBottom={!!hasReachedBottom}
                messageToSign={signMessageState.messageToSign}
                responsiveSizeMultiplier={responsiveSizeMultiplier}
              />
            }
          >
            {humanizedMessage?.warnings?.map((warning) => {
              return (
                <Label
                  size="lg"
                  key={warning.content}
                  text={warning.content}
                  type="warning"
                  style={spacings.mlMd}
                />
              )
            })}
          </ExpandableCard>
          )}
        </View>
        {(signMessageState as any).signingKeyType &&
          (signMessageState as any).signingKeyType !== 'internal' &&
          (signMessageState as any).signingKeyType !== 'qr' && (
            <HardwareWalletSigningModal
              keyType={(signMessageState as any).signingKeyType}
              isVisible={signStatus === 'LOADING'}
              onBackdropPress={() => dispatch({ type: 'MAIN_CONTROLLER_SIGN_MESSAGE_RESET' })}
            />
          )}
        {(signMessageState as any).signingKeyType === 'qr' &&
          currentRequest &&
          signingStep !== 'idle' && (
            <QrSigningFlowScreen
              isVisible={shouldDisplayQrSigningModal}
              handleClose={handleQrSigningFlowOnClosePressed}
              currentRequest={currentRequest}
              signingStep={signingStep}
              signingRequest={null}
              onContinue={handleQrSingingFlowOnContinuePressed}
              submitSignatureResponse={handleQrSigningFlowSubmitSignatureResponse}
              onReject={handleQrSigningFlowOnRejectPressed}
              handleQrSigningFlowOnBackPressed={handleQrSigningFlowOnBackPressed}
            />
          )}
        {shouldDisplayLedgerConnectModal && (
          <LedgerConnectModal
            isVisible={!isLedgerConnected}
            handleOnConnect={handleDismissLedgerConnectModal}
            handleClose={handleDismissLedgerConnectModal}
            displayOptionToAuthorize={false}
          />
        )}
      </View>
    </TabLayoutWrapperMainContent>
  )
}

export default React.memo(Main)
